interface MeterBarProps {
  /** Value from 0 to 1 (e.g. confidence 0.78 = 78%). */
  value: number;
  /** Optional explicit label; defaults to the value formatted as a percentage. */
  label?: string;
  /** Width of the track in pixels. Defaults to the 80px standard set in global.css. */
  width?: number;
}

/**
 * Fill color interpolates from --negative-500 through --status-warning to
 * --positive-500 as value moves from 0 to 1, so a glance at the bar's
 * color communicates quality without reading the number — used for
 * confidence meters (Signals page) and could be reused for win-rate
 * display once Phase 2/3 components are built.
 */
function interpolateColor(value: number): string {
  const clamped = Math.max(0, Math.min(1, value));
  if (clamped < 0.5) {
    // negative -> warning across the first half
    return `color-mix(in srgb, var(--negative-500) ${(1 - clamped * 2) * 100}%, var(--status-warning) ${clamped * 2 * 100}%)`;
  }
  // warning -> positive across the second half
  const t = (clamped - 0.5) * 2;
  return `color-mix(in srgb, var(--status-warning) ${(1 - t) * 100}%, var(--positive-500) ${t * 100}%)`;
}

export function MeterBar({ value, label, width = 80 }: MeterBarProps) {
  const clamped = Math.max(0, Math.min(1, value));
  const percentLabel = label ?? `${Math.round(clamped * 100)}%`;

  return (
    <span className="meter-bar">
      <span
        className="meter-bar-track"
        style={{ width }}
        role="meter"
        aria-valuenow={Math.round(clamped * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={percentLabel}
      >
        <span
          className="meter-bar-fill"
          style={{
            width: `${clamped * 100}%`,
            background: interpolateColor(clamped),
          }}
        />
      </span>
      <span className="meter-bar-label font-numeric">{percentLabel}</span>
    </span>
  );
}
